import { FC, isValidElement, useState } from 'react';
import { Autocomplete, AutocompleteRenderInputParams, Chip, Svg } from '@hopskipdrive/react-components';

import { ProfileImage } from '../../../../shared/components/ProfileImage';
import { ProfileInitials } from '../../../../shared/components/ProfileInitials';
import { colorPalette, colorTokens } from '../../../../shared/components/variables';
import { RiderOption } from '../../../hooks/useRiderGroupedOptions';

interface SelectRiderProps {
  riderOptions: RiderOption[];
  isFetching: boolean;
  selectedRiders: RiderOption[];
  handleSelectedRiders: (_: Event, value: RiderOption) => void;
  handleRemoveSelectedRiders: (option: RiderOption) => void;
  onRiderClick: (riderSlug: string) => void;
  renderInput: (params: AutocompleteRenderInputParams) => JSX.Element;
}

export const RiderDropdownSearch: FC<SelectRiderProps> = ({
  riderOptions,
  isFetching,
  selectedRiders,
  handleSelectedRiders,
  handleRemoveSelectedRiders,
  onRiderClick,
  renderInput,
}) => {
  const [riderInputValue, setRiderInputValue] = useState<string>('');

  const handleRenderInput = (params: AutocompleteRenderInputParams) => {
    if (params.InputProps.startAdornment && isValidElement(params.InputProps.startAdornment)) {
      params.InputProps.startAdornment = params.InputProps.startAdornment.props.children.slice(1);
    }

    params.inputProps.placeholder = selectedRiders.length ? '' : 'Select riders';

    return renderInput(params);
  };

  return (
    <Autocomplete
      multiple
      limitTags={4}
      options={riderOptions || []}
      isLoading={isFetching}
      inputValue={riderInputValue}
      filterOptions={(options) => options}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.label || '')}
      groupBy={(option) => option.group}
      value={selectedRiders}
      onInputChange={(_, value) => {
        setRiderInputValue(value);
      }}
      onChange={handleSelectedRiders}
      isOptionEqualToValue={(option, value) => {
        return option.value === value.value;
      }}
      renderTags={(tagValues, getTagProps) => {
        return tagValues.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });

          return (
            <Chip
              {...tagProps}
              type="round"
              key={option.value}
              label={option.label}
              icon={
                option.profilePictureUrl ? (
                  <ProfileImage imgSrc={option.profilePictureUrl} size={23} />
                ) : (
                  <ProfileInitials key={option.value} fullName={option.label} size={23} />
                )
              }
              style={{
                color: colorTokens.primary.main,
                background: colorPalette.navy980,
                minHeight: '27px',
                border: `1px solid ${colorTokens.primary.outlineBorder}`,
                padding: '2px 4px',
              }}
              onClick={() => onRiderClick(option.slug)}
              deleteIcon={<Svg icon="cancel-filled" style={{ color: colorTokens.primary.outlineBorder }} />}
              onDelete={() => {
                handleRemoveSelectedRiders(option);
              }}
            />
          );
        });
      }}
      renderInput={handleRenderInput}
    />
  );
};
